import React, { useEffect, useMemo, useRef, useState } from "react";
import classes from "./PriceConfigurations.module.css";
import { AgGridReact } from "ag-grid-react";
import { ReactComponent as InfoIcon } from "../../assets/info-circle-filled.svg";
import { Button, Divider, Modal } from "antd";
import { ReactComponent as OrderBox } from "../../assets/order-box.svg";
import api from "../../utils/axios";
import { getSellerOptions } from "../../api/client-reports/sellers";
import { ColDef, RowValueChangedEvent } from "ag-grid-community";
import NumericEditor from "../../components/NumericEditor";
import { toast } from "react-toastify";
import ModalSelector from "../../components/form/ModalSelector";
import useLang from "../../hooks/useLang";
import DefaultSwitch from "../../components/form/DefaultSwitch";
import DefaultSelect from "../../components/form/DefaultSelect";
import { VALUE_BIG, VOLUME_BIG, WEIGHT_BIG } from "./PackageBIG";
import { VALUE_VIP, VOLUME_VIP, WEIGHT_VIP } from "./PackageVIP";
import {
	VALUE_SUPERVIP,
	VOLUME_SUPERVIP,
	WEIGHT_SUPERVIP,
} from "./PackageSUPERVIP";
import { VALUE_TREGU, VOLUME_TREGU, WEIGHT_TREGU } from "./PackageTREGU";
import useCurrencies from "../../hooks/useCurrencies";
import useZones from "../../hooks/useZones";
import { COUNTRY_AL } from "../../api/currency";
import { currencySymbol } from "../../utils/formatters";

type PackageValueDefaults = {
	id: number;
	from?: number;
	to?: number | null;
	percentage?: number;
	number?: number;
	zoneId: number;
};

type PackageWeightDefaults = {
	id: number;
	from?: number;
	to?: number | null;
	price?: number;
	number?: number;
	zoneId: number;
};
type PackagReturnValuesDefaults = {
	id: number;
	from?: number;
	to?: number | null;
	price?: number;
	percentage?: number;
	valueType?: number;
	number?: number;
	zoneId: number;
};
type OtherCountriesPackageDefaults = {
	countryId: number;
	country: string;
	packageWeightDefaults: PackageWeightDefaults[];
	packageValueDefaults: PackageValueDefaults[];
	packageVolumeDefaults: PackageWeightDefaults[];
	packageReturnValueDefaults: PackagReturnValuesDefaults[];
};
type Seller = {
	id: number;
	firstName?: string | null;
	lastName?: string | null;
	username?: string | null;
	uniqueSellerIdentifier?: string | null;
};
const PriceConfigurations = () => {
	const [packageWeightColumns, setPackageWeightColumns] =
		useState<ColDef[]>();
	const [packageVolumeColumns, setPackageVolumeColumns] =
		useState<ColDef[]>();
	const [packageValueColumns, setPackageValueColumns] = useState<ColDef[]>();
	const [packageReturnValueColumns, setPackageReturnValueColumns] =
		useState<ColDef[]>();
	const [otherPackageWeightColumns, setOtherPackageWeightColumns] =
		useState<ColDef[]>();
	const [otherPackageVolumeColumns, setOtherPackageVolumeColumns] =
		useState<ColDef[]>();
	const [otherPackageValueColumns, setOtherPackageValueColumns] =
		useState<ColDef[]>();
	const [otherPackageReturnValueColumns, setOtherPackageReturnValueColumns] =
		useState<ColDef[]>();
	const [otherCountriesPackageDefaults, setOtherCountriesPackageDefaults] =
		useState<OtherCountriesPackageDefaults[]>();
	const [openModal, setOpenModal] = useState(false);
	const [packageWeightDefaults, setPackageWeightDefaults] = useState<
		PackageWeightDefaults[]
	>([]);
	const [packageValueDefaults, setPackageValueDefaults] = useState<
		PackageValueDefaults[]
	>([]);
	const [packageVolumeDefaults, setPackageVolumeDefaults] = useState<
		PackageWeightDefaults[]
	>([]);
	const [packageReturnValueDefaults, setPackageReturnValueDefaults] =
		useState<PackagReturnValuesDefaults[]>([]);

	const [editedWeightDefaults, setEditedWeightDefaults] = useState<
		PackageWeightDefaults[]
	>([]);
	const [editedVolumeDefaults, setEditedVolumeDefaults] = useState<
		PackageWeightDefaults[]
	>([]);
	const [editedValueDefaults, setEditedValueDefaults] = useState<
		PackageValueDefaults[]
	>([]);
	const [editedReturnValueDefaults, setEditedReturnValueDefaults] = useState<
		PackagReturnValuesDefaults[]
	>([]);

	const [configurationModalOpen, setConfigurationModalOpen] =
		useState<boolean>(false);
	const [packageBIG, setPackageBIG] = useState<boolean>(false);
	const [packageVIP, setPackageVIP] = useState<boolean>(false);
	const [packageSUPERVIP, setPackageSUPERVIP] = useState<boolean>(false);
	const [packageTREGU, setPackageTREGU] = useState<boolean>(false);

	const [sellers, setSellers] = useState<Seller[]>([]);
	const [selectedSeller, setSelectedSeller] = useState(null);

	const [seller, setSeller] = useState<Seller>();
	const [selectedSellerCredentials, setSelectedSellerCredentials] =
		useState<any>();
	const { strings } = useLang("priceConfiguration");

	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
			autoHeaderHeight: true,
		}),
		[]
	);

	let grid = useRef(null);

	// Which currency's tariff template is on screen. Where the deployment is single-currency this
	// stays undefined and the API ignores it, so the page behaves exactly as it did before.
	const {
		currencies,
		dualCurrencyEnabled,
		defaultCurrencyId,
		countryId,
		loading: currenciesLoading,
	} = useCurrencies();
	const [currencyId, setCurrencyId] = useState<number | undefined>(undefined);

	// Which zone's tariff template is on screen. `undefined` means the whole country, and it is the
	// default: no `zoneId` is then sent on reads and none on writes, so every path behaves exactly
	// as it did before per-zone tariffs existed. Axios drops undefined params, so the requests are
	// byte-identical.
	const [zoneId, setZoneId] = useState<number | undefined>(undefined);
	const { tariffZonesOf } = useZones();
	const tariffZones = useMemo(
		() => tariffZonesOf(countryId),
		[tariffZonesOf, countryId]
	);

	// A city with no sections beneath it is never on any order's zone chain, so a price saved
	// against it applies to nothing. Kept selectable — sections can be added later — but named, so
	// nobody configures one believing it took effect.
	const zoneOptions = useMemo(
		() =>
			tariffZones.map((zone) =>
				zone.reachable
					? { value: zone.value, label: zone.label }
					: {
							value: zone.value,
							label: `${zone.label} — ${
								strings?.zone?.noSections ?? "pa sektorë"
							}`,
					  }
			),
		[tariffZones, strings]
	);

	const selectedZoneUnreachable = useMemo(
		() =>
			zoneId !== undefined &&
			tariffZones.some(
				(zone) => zone.value === zoneId && !zone.reachable
			),
		[tariffZones, zoneId]
	);

	/**
	 * Per-zone tariffs exist on PostmanAL only.
	 *
	 * Both SQL migrations target AL (`targets.txt`), so Kosovo's and Macedonia's lookups still match
	 * `ZoneId` exactly instead of walking the zone's parent chain. Sending them a city id returns no
	 * bands at all and empties every grid — so the selector must not be offered there.
	 *
	 * Widen this the day the migrations are applied to the other databases; it is not tied to
	 * dual currency, it just happens to share a country today.
	 */
	const zoneTariffsEnabled =
		countryId === COUNTRY_AL && zoneOptions.length > 0;

	/**
	 * True only for a zone narrower than the country. Selecting the country explicitly, or clearing
	 * the selector, both mean the national template — nothing is inherited and the ready-made
	 * package tables still apply.
	 */
	const subCountryZoneSelected = zoneId !== undefined && zoneId !== countryId;

	/**
	 * A row whose `zoneId` is not the selected zone was inherited — from an ancestor zone, or from
	 * the seller's country-wide contract. With no zone selected nothing is inherited.
	 *
	 * This lives on the grid rather than on the column defs because the four column arrays are the
	 * same instances handed to the other-countries grids, which have no zone concept.
	 */
	const inheritedRowClassRules = useMemo(
		() => ({
			[classes.inheritedRow]: (params: any) =>
				zoneId !== undefined && params.data?.zoneId !== zoneId,
		}),
		[zoneId]
	);

	useEffect(() => {
		if (!currenciesLoading && defaultCurrencyId) {
			setCurrencyId(defaultCurrencyId);
		}
	}, [currenciesLoading, defaultCurrencyId]);

	const currencyOptions = useMemo(
		() =>
			currencies.map((c) => ({
				value: c.id,
				label: `${c.description}${c.symbol ? ` (${c.symbol})` : ""}`,
			})),
		[currencies]
	);

	// The currency is threaded explicitly rather than read from state inside the loaders: the
	// selector's onChange refetches immediately, before its setState has landed. The zone is
	// threaded the same way, for the same reason.
	const getPackageWeightDefaults = async (
		seller?: Seller,
		currency?: number,
		zone?: number
	) => {
		try {
			const response = await api.get("packagedefaults/weight-list", {
				params: {
					sellerId: seller?.id,
					currencyId: currency,
					zoneId: zone,
				},
			});
			setPackageWeightDefaults(response.data);
		} catch (error: any) {
			console.log(error);
		}
	};
	const getPackageValueDefaults = async (
		seller?: Seller,
		currency?: number,
		zone?: number
	) => {
		try {
			const response = await api.get("packagedefaults/value-list", {
				params: {
					sellerId: seller?.id,
					currencyId: currency,
					zoneId: zone,
				},
			});
			setPackageValueDefaults(response.data);
		} catch (error: any) {
			console.log(error);
		}
	};
	const getPackageVolumeDefaults = async (
		seller?: Seller,
		currency?: number,
		zone?: number
	) => {
		try {
			const response = await api.get("packagedefaults/volume-list", {
				params: {
					sellerId: seller?.id,
					currencyId: currency,
					zoneId: zone,
				},
			});
			setPackageVolumeDefaults(response.data);
		} catch (error: any) {
			console.log(error);
		}
	};
	const getReturnValueDefaults = async (
		seller?: Seller,
		currency?: number,
		zone?: number
	) => {
		try {
			const response = await api.get(
				"packagedefaults/return-value-list",
				{
					params: {
						sellerId: seller?.id,
						currencyId: currency,
						zoneId: zone,
					},
				}
			);
			setPackageReturnValueDefaults(response.data);
		} catch (error: any) {
			console.log(error);
		}
	};

	const getOtherCountriesPackageDefaults = async (
		seller?: Seller,
		currency?: number
	) => {
		try {
			const response = await api.get(
				"packagedefaults/other-countries-list",
				{
					params: { sellerId: seller?.id, currencyId: currency },
				}
			);
			setOtherCountriesPackageDefaults(response.data);
		} catch (error: any) {
			console.log(error);
		}
	};
	// The light client list. `user/sellers` runs GetClients_sp (~11 s, lifetime
	// balances per seller) and ran again every time this tab was opened.
	const getSellers = async () => {
		try {
			setSellers(await getSellerOptions());
		} catch (error: any) {
			console.log(error);
		}
	};
	const getPackageDefaults = async (
		seller?: Seller,
		currency?: number,
		zone?: number
	) => {
		const effectiveCurrency = currency ?? currencyId;
		// Callers that omit the zone mean "whatever is on screen", not "the country" - four of them
		// below rely on this. Without the fallback they would silently reset the grids to the
		// country template.
		const effectiveZone = zone ?? zoneId;
		await getPackageWeightDefaults(
			seller,
			effectiveCurrency,
			effectiveZone
		);
		await getPackageValueDefaults(seller, effectiveCurrency, effectiveZone);
		await getPackageVolumeDefaults(
			seller,
			effectiveCurrency,
			effectiveZone
		);
		await getReturnValueDefaults(seller, effectiveCurrency, effectiveZone);
		// Deliberately no zone: this one means "every country except mine".
		await getOtherCountriesPackageDefaults(seller, effectiveCurrency);
	};

	const onCurrencySelected = async (value: number) => {
		setCurrencyId(value);
		// Edits belong to the currency they were made in; switching discards anything unsaved.
		setEditedValueDefaults([]);
		setEditedVolumeDefaults([]);
		setEditedWeightDefaults([]);
		setEditedReturnValueDefaults([]);
		await getPackageDefaults(seller, value);
	};

	const onZoneSelected = async (value?: number) => {
		setZoneId(value);
		// Edits belong to the zone they were made in; switching discards anything unsaved.
		setEditedValueDefaults([]);
		setEditedVolumeDefaults([]);
		setEditedWeightDefaults([]);
		setEditedReturnValueDefaults([]);
		await getPackageDefaults(seller, undefined, value);
	};

	// Waits for the currency list so the first fetch already carries the right currency, rather
	// than loading one template and immediately replacing it with another.
	useEffect(() => {
		if (!currenciesLoading) {
			getPackageDefaults(undefined, defaultCurrencyId || undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currenciesLoading, defaultCurrencyId]);

	// useEffect(() => {
	// 	getOtherCountriesPackageDefaults();
	// }, []);

	useEffect(() => {
		getSellers();
	}, []);

	useEffect(() => {
		const packageDefaultColumns = [
			{
				field: "from",
				headerName: strings?.grid.from,
				width: 100,
			},
			{
				field: "to",
				headerName: strings?.grid.to,
				width: 100,
			},
		];
		const colsWithPrice = [
			...packageDefaultColumns,
			{
				field: "price",
				headerName: strings?.grid.price,
				width: 130,
				cellEditor: NumericEditor,
				cellEditorParams: {
					suffix: currencySymbol(currencyId),
				},
				cellStyle: {
					height: "100%",
				},
				editable: true,
			},
		];
		const colsWithPercentage = [
			...packageDefaultColumns,
			{
				field: "percentage",
				headerName: strings?.grid.percentage,
				cellEditor: NumericEditor,
				width: 130,
				cellEditorParams: {
					suffix: "%",
				},
				cellStyle: {
					height: "100%",
				},
				editable: true,
			},
		];
		const colsWithValue = [
			...packageDefaultColumns,
			{
				field: "price",
				headerName: strings?.grid.price,
				width: 130,
				cellEditor: NumericEditor,
				cellEditorParams: {
					suffix: currencySymbol(currencyId),
				},
				cellStyle: {
					height: "100%",
				},
				editable: (params: any) => params.data.valueType === 1,
			},
			{
				field: "percentage",
				headerName: strings?.grid.percentage,
				cellEditor: NumericEditor,
				width: 130,
				cellEditorParams: {
					suffix: "%",
				},
				cellStyle: {
					height: "100%",
				},
				editable: (params: any) => params.data.valueType === 2,
			},
		];
		const colsWithPriceAndPercentage = [
			...packageDefaultColumns,
			{
				field: "price",
				headerName: strings?.grid.price,
				width: 130,
				cellEditor: NumericEditor,
				cellEditorParams: {
					suffix: currencySymbol(currencyId),
				},
				cellStyle: {
					height: "100%",
				},
				editable: (params: any) => params.data.valueType === 1,
			},
			{
				field: "percentage",
				headerName: strings?.grid.percentage,
				cellEditor: NumericEditor,
				width: 130,
				cellEditorParams: {
					suffix: "%",
				},
				cellStyle: {
					height: "100%",
				},
				editable: (params: any) => params.data.valueType === 2,
			},
		];
		setOtherPackageVolumeColumns(colsWithPrice);
		setOtherPackageWeightColumns(colsWithPrice);
		setOtherPackageValueColumns(colsWithPercentage);
		setOtherPackageReturnValueColumns(colsWithValue);
		setPackageVolumeColumns(colsWithPrice);
		setPackageWeightColumns(colsWithPrice);
		setPackageValueColumns(colsWithPercentage);
		setPackageReturnValueColumns(colsWithPriceAndPercentage);
	}, [setPackageWeightColumns, strings, currencyId]);

	const modalHandler = () => {
		setOpenModal(!openModal);
	};

	const updateArray = (prev: any[], data: any) => {
		if (prev.some((v) => v.id === data.id)) {
			return prev.map((v) => (v.id === data.id ? data : v));
		} else {
			return [...prev, data];
		}
	};

	const onVolumeRowValueChanged = (params: RowValueChangedEvent) => {
		setEditedVolumeDefaults((prev) => updateArray(prev, params.data));
	};
	const onWeightRowValueChanged = (params: RowValueChangedEvent) => {
		setEditedWeightDefaults((prev) => updateArray(prev, params.data));
	};
	const onValueRowValueChanged = (params: RowValueChangedEvent) => {
		setEditedValueDefaults((prev) => updateArray(prev, params.data));
	};
	const onReturnValueRowValueChanged = (params: RowValueChangedEvent) => {
		setEditedReturnValueDefaults((prev) => updateArray(prev, params.data));
	};

	/**
	 * Splits an edit buffer by which grid the row was edited in.
	 *
	 * The other-countries grids reuse the four `onRowValueChanged` handlers above, so an edited
	 * Kosovo row lands in the same buffer as an edited Tirana one. Those cannot go out in one
	 * request: the payload-level `zoneId` overrides every item's `zoneId`, which is precisely what
	 * makes editing a zone safe — and precisely what would rewrite another country's row to that
	 * zone.
	 *
	 * The main grids' current `rowData` is the membership test. Ids are unique per row within a
	 * table, so the split is exact and needs no knowledge of the zone hierarchy.
	 */
	const splitByGrid = <T extends { id: number }>(
		edited: T[],
		visibleInMainGrid: { id: number }[]
	) => {
		const ids = new Set(visibleInMainGrid.map((row) => row.id));
		return {
			mine: edited.filter((row) => ids.has(row.id)),
			others: edited.filter((row) => !ids.has(row.id)),
		};
	};

	const buildUpdateBody = (
		valueDefaults: PackageValueDefaults[],
		weightDefaults: PackageWeightDefaults[],
		volumeDefaults: PackageWeightDefaults[],
		returnValueDefaults: PackagReturnValuesDefaults[],
		payloadZoneId?: number
	) => ({
		valueDefaults: valueDefaults.map((v) => ({
			id: v.id,
			percentage: v.percentage,
			number: v.number,
			zoneId: v.zoneId,
		})),
		weightDefaults: weightDefaults.map((v) => ({
			id: v.id,
			price: v.price,
			number: v.number,
			zoneId: v.zoneId,
		})),
		volumeDefaults: volumeDefaults.map((v) => ({
			id: v.id,
			price: v.price,
			number: v.number,
			zoneId: v.zoneId,
		})),
		returnValueDefaults: returnValueDefaults.map((v) => ({
			id: v.id,
			price: v.price,
			percentage: v.percentage,
			number: v.number,
			zoneId: v.zoneId,
		})),
		sellerId: seller?.id,
		currencyId,
		// Authoritative when set — the API overrides every item's zoneId with it. That is required,
		// because a read for a zone returns the bands that zone does not define as the COUNTRY's
		// rows, carrying the country's zoneId. Echoing those back unchanged would rewrite the
		// national template while the operator believed they were editing a city.
		zoneId: payloadZoneId,
	});

	const onSubmit = async () => {
		const value = splitByGrid(editedValueDefaults, packageValueDefaults);
		const weight = splitByGrid(editedWeightDefaults, packageWeightDefaults);
		const volume = splitByGrid(editedVolumeDefaults, packageVolumeDefaults);
		const rejection = splitByGrid(
			editedReturnValueDefaults,
			packageReturnValueDefaults
		);

		const count = (pick: "mine" | "others") =>
			value[pick].length +
			weight[pick].length +
			volume[pick].length +
			rejection[pick].length;

		try {
			if (count("mine") > 0) {
				await api.post(
					"packagedefaults/update",
					buildUpdateBody(
						value.mine,
						weight.mine,
						volume.mine,
						rejection.mine,
						zoneId
					)
				);
			}
			// Rows edited in the other-countries grids keep their own zone: no payload zoneId, so
			// the API leaves each item where it already was.
			if (count("others") > 0) {
				await api.post(
					"packagedefaults/update",
					buildUpdateBody(
						value.others,
						weight.others,
						volume.others,
						rejection.others,
						undefined
					)
				);
			}
			setEditedValueDefaults([]);
			setEditedVolumeDefaults([]);
			setEditedWeightDefaults([]);
			setEditedReturnValueDefaults([]);
			toast.success(strings?.toast.success);
			await getPackageDefaults(seller);
		} catch (e) {
			console.log(e);
		}
	};

	const onSellerSelected = async (row: Seller) => {
		setSeller(row);
		await getPackageDefaults(row);
	};

	const onSellerDeleted = async () => {
		setSeller(undefined);
		setEditedValueDefaults([]);
		setEditedVolumeDefaults([]);
		setEditedWeightDefaults([]);
		setEditedReturnValueDefaults([]);
		await getPackageDefaults();
		await getOtherCountriesPackageDefaults();
	};

	const renderSellerOptions = () => {
		return sellers.map((sellerItem: Seller, index: number) => ({
			value: `${sellerItem.id}-${index}`,
			label: `${sellerItem.username}  (${
				sellerItem.uniqueSellerIdentifier ?? ""
			})`,
		}));
	};

	const [selectedPackageString, setSelectedPackageString] = useState("");
	const changeSwitch = (selectedPackage: string) => {
		setPackageBIG(selectedPackage === "BIG");
		setPackageVIP(selectedPackage === "VIP");
		setPackageSUPERVIP(selectedPackage === "SUPERVIP");
		setPackageTREGU(selectedPackage === "TREGU");
		switch (selectedPackage) {
			case "BIG":
			case "VIP":
			case "SUPERVIP":
			case "TREGU":
				setSelectedPackageString(selectedPackage);
				break;
			default:
				setSelectedPackageString("");
		}
	};
	const updateConfirgurationsPerSellerAndPackage = async () => {
		// The trigger button is disabled while a sub-country zone is selected; this is the second
		// lock. Each row in the static tables below carries its own hardcoded zoneId (1, 2 and 3 —
		// all three countries), so applying one while looking at a city would write a tariff for a
		// zone other than the one on screen. Note this save deliberately sends no payload zoneId,
		// which is what lets those per-row zones survive.
		if (subCountryZoneSelected) {
			return;
		}
		const packageDefaults: any = {
			BIG: {
				value: VALUE_BIG,
				volume: VOLUME_BIG,
				weight: WEIGHT_BIG,
			},
			VIP: {
				value: VALUE_VIP,
				volume: VOLUME_VIP,
				weight: WEIGHT_VIP,
			},
			SUPERVIP: {
				value: VALUE_SUPERVIP,
				volume: VOLUME_SUPERVIP,
				weight: WEIGHT_SUPERVIP,
			},
			TREGU: {
				value: VALUE_TREGU,
				volume: VOLUME_TREGU,
				weight: WEIGHT_TREGU,
			},
		};

		if (packageDefaults[selectedPackageString]) {
			const packageData = packageDefaults[selectedPackageString];
			const value = packageData.value;
			const volume = packageData.volume;
			const weight = packageData.weight;

			try {
				await api.post("packagedefaults/update-packages", {
					valueDefaults: value.map((v: any) => ({
						id: v.id,
						percentage: v.percentage,
						number: v.number,
						zoneId: v.zoneId,
					})),
					weightDefaults: weight.map((v: any) => ({
						id: v.id,
						price: v.price,
						number: v.number,
						zoneId: v.zoneId,
					})),
					volumeDefaults: volume.map((v: any) => ({
						id: v.id,
						price: v.price,
						number: v.number,
						zoneId: v.zoneId,
					})),
					sellerIds: selectedSeller,
					// The static BIG/VIP/SUPERVIP/TREGU tables are priced in one currency, so the
					// bulk apply writes into whichever template is currently on screen.
					currencyId,
				});
				setEditedValueDefaults([]);
				setEditedVolumeDefaults([]);
				setEditedWeightDefaults([]);
				setEditedReturnValueDefaults([]);
				setSelectedSeller(null);
				toast.success(strings?.toast.success);
				await getPackageDefaults(seller);
				setConfigurationModalOpen(false);
			} catch (e) {
				console.log(e);
			}
		}
	};

	const clearModalConfigurationStates = () => {
		setEditedValueDefaults([]);
		setEditedVolumeDefaults([]);
		setEditedWeightDefaults([]);
		setEditedReturnValueDefaults([]);
		setPackageBIG(false);
		setPackageVIP(false);
		setPackageSUPERVIP(false);
		setPackageTREGU(false);
		setSelectedSeller(null);
		setConfigurationModalOpen(false);
	};

	const onSellerForPackageSelected = (sellerIds: any) => {
		const filteredSellerIds = sellerIds?.map((id: any) => id.split("-")[0]);
		setSelectedSeller(filteredSellerIds);
		setSelectedSellerCredentials(sellerIds);
	};

	return (
		<>
			<div className={classes.mainContainer}>
				<h1 className={classes.title}>{strings?.titles.main}</h1>
				<div className="flex items-center gap-6 flex-wrap">
					<ModalSelector
						buttonLabel={strings?.button.seller}
						tableData={sellers}
						onRowSelected={onSellerSelected}
						onRowDeleted={onSellerDeleted}
						columnsToShow={["firstName", "lastName"]}
					/>
					{/* Only where more than one currency is actually supported — elsewhere the API
					    ignores the choice, so offering one would be a lie. */}
					{dualCurrencyEnabled && (
						<DefaultSelect
							label={strings?.currency?.label ?? "Monedha"}
							value={currencyId}
							options={currencyOptions}
							onChange={onCurrencySelected}
							style={{ width: 200 }}
						/>
					)}
					{/* Clearing this returns to the country-wide template, which is the default and
					    the pre-zone behaviour. Only rendered where there is a zone to pick.
					    No onClear: antd already fires onChange(undefined) on clear, and wiring both
					    would run the whole reload twice. */}
					{zoneTariffsEnabled && (
						<DefaultSelect
							label={strings?.zone?.label ?? "Zona"}
							value={zoneId}
							options={zoneOptions}
							onChange={onZoneSelected}
							placeholder={
								strings?.zone?.wholeCountry ?? "Tërë vendi"
							}
							allowClear
							style={{ width: 240 }}
						/>
					)}
				</div>
				{dualCurrencyEnabled && (
					<div className="text-sm text-gray-500 -mt-2">
						{strings?.currency?.hint ??
							"Po shfaqen dhe editohen çmimet vetëm për monedhën e zgjedhur."}
					</div>
				)}
				{subCountryZoneSelected && (
					<div className="text-sm text-gray-500 -mt-2">
						{strings?.zone?.hint ??
							"Çmimet e ruajtura për këtë zonë bëhen specifike për zonën dhe nuk ndjekin më çmimet e vendit. Çmimi i negociuar i një klienti mbetet mbi çmimin e zonës."}
					</div>
				)}
				{selectedZoneUnreachable && (
					<div className="text-sm text-amber-600 -mt-2">
						{strings?.zone?.noSectionsWarning ??
							"Kjo zonë nuk ka asnjë sektor, prandaj asnjë porosi nuk kalon nëpër të. Çmimet e ruajtura këtu nuk do të aplikohen derisa të shtohet një sektor."}
					</div>
				)}

				<div className="flex w-full">
					{/* The BIG/VIP/SUPERVIP/TREGU tables carry their own hardcoded zoneId values, so
					    applying one while a city or section is selected would write a different
					    zone's tariff than the operator is looking at. */}
					<Button
						className="button2"
						disabled={subCountryZoneSelected}
						title={
							subCountryZoneSelected
								? strings?.zone?.packagesDisabled ??
								  "Paketat e gatshme aplikohen vetëm për tërë vendin. Hiq zgjedhjen e zonës për t'i përdorur."
								: undefined
						}
						onClick={() => setConfigurationModalOpen(true)}
					>
						Paketat
					</Button>
				</div>

				<div className={classes.firstContainer}>
					<div className="w-full">
						<div className={classes.packageTitle}>
							{strings?.titles.volume}
						</div>
						<div
							className="ag-theme-alpine"
							style={{
								width: "80%",
								height: 400,
							}}
						>
							<AgGridReact
								rowData={packageVolumeDefaults}
								rowClassRules={inheritedRowClassRules}
								columnDefs={packageVolumeColumns}
								defaultColDef={defaultColDef}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								ref={grid}
								editType={"fullRow"}
								rowHeight={40}
								onRowValueChanged={onVolumeRowValueChanged}
							/>
						</div>
					</div>
					<div className="w-full">
						<div className={classes.packageTitle}>
							{strings?.titles.weight}
						</div>
						<div
							className="ag-theme-alpine"
							style={{
								width: "80%",
								height: 400,
							}}
						>
							<AgGridReact
								rowData={packageWeightDefaults}
								rowClassRules={inheritedRowClassRules}
								columnDefs={packageWeightColumns}
								defaultColDef={defaultColDef}
								suppressCopyRowsToClipboard={true}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								ref={grid}
								editType={"fullRow"}
								rowHeight={40}
								onRowValueChanged={onWeightRowValueChanged}
							/>
						</div>
					</div>
					<div className="w-full">
						<div className={classes.packageTitle}>
							{strings?.titles.value}
						</div>
						<div
							className="ag-theme-alpine"
							style={{
								width: "80%",
								height: 400,
							}}
						>
							<AgGridReact
								rowData={packageValueDefaults}
								rowClassRules={inheritedRowClassRules}
								columnDefs={packageValueColumns}
								defaultColDef={defaultColDef}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								editType={"fullRow"}
								ref={grid}
								rowHeight={40}
								onRowValueChanged={onValueRowValueChanged}
							/>
						</div>
					</div>
					<div className="w-full">
						<div className={classes.packageTitle}>
							{strings?.titles.returnValue}
						</div>
						<div
							className="ag-theme-alpine"
							style={{
								width: "80%",
								height: 400,
							}}
						>
							<AgGridReact
								rowData={packageReturnValueDefaults}
								rowClassRules={inheritedRowClassRules}
								columnDefs={packageReturnValueColumns}
								defaultColDef={defaultColDef}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								editType={"fullRow"}
								ref={grid}
								rowHeight={40}
								onRowValueChanged={onReturnValueRowValueChanged}
							/>
						</div>
					</div>
				</div>

				<div className={classes.secondContainer}>
					<InfoIcon />
					<div>
						<p className={classes.secondTitle}>
							{strings?.info.dimension}
						</p>
						<p className={classes.info}>
							{strings?.info.clickForMore}
						</p>
					</div>
				</div>
				<div className={classes.thirdContainer}>
					<h1 className={classes.title}>
						{strings?.titles.countryConfig}
					</h1>
					<div>
						{otherCountriesPackageDefaults?.map((opd, key) => {
							return (
								<div
									className={classes.otherCountries}
									key={key}
								>
									<div className={classes.country}>
										{opd.country}
									</div>

									<div
										className={`${classes.firstContainer} ${classes.otherGridContainer}`}
									>
										<div className="w-full">
											<div
												className={classes.packageTitle}
											>
												{strings?.titles.volume}
											</div>
											<div
												className="ag-theme-alpine"
												style={{
													width: "100%",
													height: 300,
												}}
											>
												<AgGridReact
													rowData={
														opd.packageVolumeDefaults
													}
													columnDefs={
														otherPackageVolumeColumns
													}
													defaultColDef={
														defaultColDef
													}
													defaultExcelExportParams={
														defaultExcelExportSettings
													}
													ref={grid}
													editType={"fullRow"}
													rowHeight={40}
													onRowValueChanged={
														onVolumeRowValueChanged
													}
												/>
											</div>
										</div>
										<div className="w-full">
											<div
												className={classes.packageTitle}
											>
												{strings?.titles.weight}
											</div>
											<div
												className="ag-theme-alpine"
												style={{
													width: "100%",
													height: 300,
												}}
											>
												<AgGridReact
													rowData={
														opd.packageWeightDefaults
													}
													columnDefs={
														otherPackageWeightColumns
													}
													defaultColDef={
														defaultColDef
													}
													suppressCopyRowsToClipboard={
														true
													}
													defaultExcelExportParams={
														defaultExcelExportSettings
													}
													ref={grid}
													editType={"fullRow"}
													rowHeight={40}
													onRowValueChanged={
														onWeightRowValueChanged
													}
												/>
											</div>
										</div>
										<div className="w-full">
											<div
												className={classes.packageTitle}
											>
												{strings?.titles.value}
											</div>
											<div
												className="ag-theme-alpine"
												style={{
													width: "100%",
													height: 300,
												}}
											>
												<AgGridReact
													rowData={
														opd.packageValueDefaults
													}
													columnDefs={
														otherPackageValueColumns
													}
													defaultColDef={
														defaultColDef
													}
													defaultExcelExportParams={
														defaultExcelExportSettings
													}
													editType={"fullRow"}
													ref={grid}
													rowHeight={40}
													onRowValueChanged={
														onValueRowValueChanged
													}
												/>
											</div>
										</div>
										<div className="w-full">
											<div
												className={classes.packageTitle}
											>
												{strings?.titles.returnValue}
											</div>
											<div
												className="ag-theme-alpine"
												style={{
													width: "100%",
													height: 300,
												}}
											>
												<AgGridReact
													rowData={
														opd.packageReturnValueDefaults
													}
													columnDefs={
														otherPackageReturnValueColumns
													}
													defaultColDef={
														defaultColDef
													}
													defaultExcelExportParams={
														defaultExcelExportSettings
													}
													ref={grid}
													editType={"fullRow"}
													rowHeight={40}
													onRowValueChanged={
														onReturnValueRowValueChanged
													}
												/>
											</div>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
			<div className="flex justify-end">
				<Button
					type="primary"
					size="large"
					// className="bg-blue-700"
					style={{ backgroundColor: "#4F16C2" }}
					onClick={onSubmit}
				>
					{strings?.button?.save}
				</Button>
			</div>
			<Modal
				open={openModal}
				onCancel={() => setOpenModal(false)}
				centered
				footer={null}
			>
				<p className={classes.modalTitle}>{strings?.modal?.title}</p>
				<div className={classes.modal}>
					<OrderBox />
				</div>
			</Modal>
			<Modal
				open={configurationModalOpen}
				onCancel={clearModalConfigurationStates}
				onOk={updateConfirgurationsPerSellerAndPackage}
				title={"Ndrysho paketen per klient"}
				okButtonProps={{ style: { backgroundColor: "#4F16C2" } }}
				okText="Ne rregull"
				cancelText="Mbyll"
				width={800}
			>
				<Divider />
				<div className="flex flex-col justify-around  gap-8 pl-6">
					<div className="flex w-full">
						<DefaultSelect
							label={"Shitesi"}
							flexDirection="col"
							alignCenter="start"
							className={"registerInput"}
							placeholder={"Zgjedh Shitesin"}
							options={renderSellerOptions()}
							value={selectedSellerCredentials ?? undefined}
							onChange={onSellerForPackageSelected}
							allowClear
							mode="multiple"
						/>
					</div>
					<div className="flex flex-col gap-4 ">
						<p className="">Paketat</p>

						<div className="flex flex-row  items-center gap-4">
							<DefaultSwitch
								label={"Paketa BIG"}
								checked={packageBIG}
								onChange={() => changeSwitch("BIG")}
							/>
							<DefaultSwitch
								label={"Paketa VIP"}
								checked={packageVIP}
								onChange={() => changeSwitch("VIP")}
							/>
							<DefaultSwitch
								label={"Paketa SUPER VIP"}
								checked={packageSUPERVIP}
								onChange={() => changeSwitch("SUPERVIP")}
							/>
							<DefaultSwitch
								label={"Paketa TREGU"}
								checked={packageTREGU}
								onChange={() => changeSwitch("TREGU")}
							/>
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default PriceConfigurations;
